import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  AutoFixHigh as SkillIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSkills } from "../../hooks/useSkills";
import { supabase } from "../../lib/supabase";
import {
  adminColors,
  textFieldStyles,
  selectMenuProps,
  dialogStyles,
  tableStyles,
  buttonStyles,
  chipStyles,
} from "./adminStyles";
import { useTranslation } from "react-i18next";

const AdminSkills = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: skills, refetch } = useSkills();

  const categories = [
    {
      value: "ai_ml",
      label: t("admin.skills.categories.aiMl"),
      color: "#F59E0B",
    },
    {
      value: "backend",
      label: t("admin.skills.categories.backend"),
      color: "#D4AF37",
    },
    {
      value: "frontend",
      label: t("admin.skills.categories.frontend"),
      color: "#0FA3A3",
    },
    {
      value: "cloud",
      label: t("admin.skills.categories.cloud"),
      color: "#0EA5E9",
    },
    {
      value: "other",
      label: t("admin.skills.categories.other"),
      color: "#9CA3AF",
    },
  ];

  const proficiencyLevels = [
    { value: "Beginner", label: t("admin.skills.levels.beginner") },
    { value: "Intermediate", label: t("admin.skills.levels.intermediate") },
    { value: "Advanced", label: t("admin.skills.levels.advanced") },
    { value: "Expert", label: t("admin.skills.levels.expert") },
  ];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    category: "ai_ml",
    level: "Intermediate",
    years_experience: 1,
    translations: {
      fr: { name: "" },
      en: { name: "" },
    },
  });
  const [selectedSkill, setSelectedSkill] = useState(null);

  const handleOpenCreate = () => {
    setSelectedSkill(null);
    setFormData({
      category: "ai_ml",
      level: "Intermediate",
      years_experience: 1,
      translations: {
        fr: { name: "" },
        en: { name: "" },
      },
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (skill) => {
    setSelectedSkill(skill);
    const frTrans =
      skill.translations?.find((t) => t.language_code === "fr") || {};
    const enTrans =
      skill.translations?.find((t) => t.language_code === "en") || {};

    setFormData({
      category: skill.category,
      level: skill.level,
      years_experience: skill.years_experience,
      translations: {
        fr: { name: frTrans.name || skill.name || "" },
        en: { name: enTrans.name || "" },
      },
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const skillData = {
        user_id: user.id,
        category: formData.category,
        level: formData.level,
        years_experience: formData.years_experience,
      };

      if (selectedSkill) {
        const { error: skillError } = await supabase
          .from("skills")
          .update(skillData)
          .eq("id", selectedSkill.id);

        if (skillError) throw skillError;

        for (const lang of ["fr", "en"]) {
          const transData = {
            skill_id: selectedSkill.id,
            language_code: lang,
            name: formData.translations[lang].name,
          };

          const { data: existing } = await supabase
            .from("skills_translations")
            .select("id")
            .eq("skill_id", selectedSkill.id)
            .eq("language_code", lang)
            .maybeSingle();

          if (existing) {
            await supabase
              .from("skills_translations")
              .update(transData)
              .eq("id", existing.id);
          } else {
            await supabase
              .from("skills_translations")
              .insert({ id: crypto.randomUUID(), ...transData });
          }
        }
      } else {
        const { data: newSkill, error: skillError } = await supabase
          .from("skills")
          .insert(skillData)
          .select()
          .single();

        if (skillError) throw skillError;

        const transInserts = [
          {
            skill_id: newSkill.id,
            language_code: "fr",
            name: formData.translations.fr.name,
          },
          {
            skill_id: newSkill.id,
            language_code: "en",
            name: formData.translations.en.name,
          },
        ].map((t) => ({ id: crypto.randomUUID(), ...t }));

        const { error: transError } = await supabase
          .from("skills_translations")
          .insert(transInserts);

        if (transError) throw transError;
      }

      await refetch();
      setDialogOpen(false);
    } catch (err) {
      console.error("Error saving skill:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{ minHeight: "100vh", bgcolor: adminColors.bg.page, pt: 12, pb: 8 }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate("/admin")}
              sx={{ color: adminColors.text.primary }}
            >
              <BackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{ color: adminColors.text.primary, fontWeight: 700 }}
            >
              {t("admin.sections.skills")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={buttonStyles.primary}
          >
            {t("admin.actions.create")}
          </Button>
        </Box>

        <TableContainer component={Paper} sx={tableStyles.container}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.name")} (FR)
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.category")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.level")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.yearsExperience")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell} align="right">
                  {t("admin.labels.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {skills?.map((skill) => (
                <TableRow key={skill.id} sx={tableStyles.row}>
                  <TableCell sx={tableStyles.bodyCell}>{skill.name}</TableCell>
                  <TableCell sx={tableStyles.bodyCell}>
                    <Chip
                      label={skill.category}
                      size="small"
                      sx={chipStyles.type("#F59E0B")}
                    />
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell}>{skill.level}</TableCell>
                  <TableCell sx={tableStyles.bodyCell}>
                    {skill.years_experience}
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell} align="right">
                    <IconButton
                      onClick={() => handleOpenEdit(skill)}
                      sx={buttonStyles.iconEdit}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton sx={buttonStyles.iconDelete}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle
            sx={{
              color: adminColors.text.primary,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <SkillIcon sx={{ color: adminColors.accent.gold }} />
            {selectedSkill
              ? `${t("admin.actions.edit")} ${t("admin.sections.skills")}`
              : `${t("admin.actions.create")} ${t("admin.sections.skills")}`}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Tabs
                value={tabValue}
                onChange={(e, v) => setTabValue(v)}
                sx={{ mb: 2 }}
              >
                <Tab label={t("admin.translations.tabs.fr")} />
                <Tab label={t("admin.translations.tabs.en")} />
              </Tabs>

              {tabValue === 0 ? (
                <TextField
                  label={`${t("admin.labels.name")} (FR)`}
                  fullWidth
                  value={formData.translations.fr.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      translations: {
                        ...formData.translations,
                        fr: { name: e.target.value },
                      },
                    })
                  }
                  sx={textFieldStyles}
                />
              ) : (
                <TextField
                  label={`${t("admin.labels.name")} (EN)`}
                  fullWidth
                  value={formData.translations.en.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      translations: {
                        ...formData.translations,
                        en: { name: e.target.value },
                      },
                    })
                  }
                  sx={textFieldStyles}
                />
              )}

              <TextField
                select
                label={t("admin.labels.category")}
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                sx={textFieldStyles}
                SelectProps={selectMenuProps}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label={t("admin.labels.level")}
                value={formData.level}
                onChange={(e) =>
                  setFormData({ ...formData, level: e.target.value })
                }
                sx={textFieldStyles}
                SelectProps={selectMenuProps}
              >
                {proficiencyLevels.map((lvl) => (
                  <MenuItem key={lvl.value} value={lvl.value}>
                    {lvl.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label={t("admin.labels.yearsExperience")}
                type="number"
                value={formData.years_experience}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    years_experience: parseInt(e.target.value) || 0,
                  })
                }
                sx={textFieldStyles}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={saving}
              sx={buttonStyles.primary}
            >
              {saving ? (
                <CircularProgress size={24} />
              ) : (
                t("admin.actions.save")
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminSkills;
